import type { BookingResponse } from '@models/landing/api/api-response.model';
import type { CountryOption, passengerContactDetails } from '@models/ui/pnr-data-model';
import type { Country } from '@models/master-data/master-data.model';
import { ContactTypeEnum, PassengerTypeEnum } from '@models/landing/master-data/master-data.model';
import { selectCountries, useMasterDataStore } from '@store/master-data.store';
import { normalizeDialCode } from '@utils/phone.util';

interface CountryOptionsWithSelected {
  options: CountryOption[];
  selectedOption: CountryOption | undefined;
}

// -------------------- Country Options Utils --------------------
const convertCountryListToOptions = (countryList: Country[]): CountryOption[] => {
  return countryList.map((country) => {
    const dialCode = normalizeDialCode(country.dialCode);

    return {
      value: country.code.toLowerCase(),
      label: country.name,
      code: dialCode,
      dialCode,
    };
  });
};

const getCountryOptionByDialCode = (
  countryList: Country[],
  dialCode: string,
): CountryOption | undefined => {
  const normalizedInput = dialCode.replace('+', '');
  const match = countryList.find(
    (country) => country.dialCode.replace('+', '') === normalizedInput,
  );
  if (!match) return undefined;

  const normalizedDialCode = normalizeDialCode(match.dialCode);

  return {
    value: match.code.toLowerCase(),
    label: match.name,
    code: normalizedDialCode,
    dialCode: normalizedDialCode,
  };
};

export const getCountryOptionsWithSelected = (
  countryList: Country[],
  dialCode: string,
): CountryOptionsWithSelected => {
  return {
    options: convertCountryListToOptions(countryList),
    selectedOption: getCountryOptionByDialCode(countryList, dialCode),
  };
};

/** Core Mapper — used by landing retrieve and confirmation flows */
export const mapContactDetails = (api: BookingResponse): passengerContactDetails[] => {
  if (!api.paxList?.length) return [];

  const countryList = selectCountries(useMasterDataStore.getState());

  return api.paxList.map((pax) => mapPaxToContactDetails(pax, countryList));
};

const mapPaxToContactDetails = (
  pax: BookingResponse['paxList'][number],
  countryList: Country[],
): passengerContactDetails => {
  const contacts = pax.contactInfo ?? [];
  const emailContact = contacts.find((c) => c.contactType === ContactTypeEnum.Email);
  const phoneContact = contacts.find((c) => c.contactType === ContactTypeEnum.MobilePhone);
  const { selectedOption, options } = getCountryOptionsWithSelected(
    countryList,
    phoneContact?.countryCode ?? '',
  );

  return {
    paxId: String(pax.resPaxId),
    isPrimaryPassenger: pax.isPrimaryPassenger,
    email: emailContact?.contactField ?? '',
    phone: phoneContact?.contactPhone ?? '',
    countryCode: phoneContact?.countryCode ?? '',
    firstName: pax.firstName ?? '',
    lastName: pax.lastName ?? '',
    passengerType: getPassengerTypeLabel(pax.passengerType ?? 0),
    countryOption: options,
    selected: selectedOption,
  };
};

/** Helper: Passenger Type Label */
const getPassengerTypeLabel = (type: number): string => {
  switch (type) {
    case PassengerTypeEnum.Adult:
      return 'Adult';
    case PassengerTypeEnum.Child:
      return 'Child';
    case PassengerTypeEnum.Infant:
      return 'Infant';
    default:
      return '';
  }
};
